import React, { useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import NtactLogo from '../../assets/NTACT Logo.svg';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import LOgo from '../../assets/Ntact.png';

// ─── PDF Styles ───
const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontSize: 13,
    fontFamily: 'Helvetica',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    borderBottom: '1px solid #ccc',
    paddingBottom: 8,
  },
  logo: { width: 100, height: 50, marginBottom: 10 },
  title: { fontSize: 20, fontWeight: 'bold' },
  sectionHeader: {
    textAlign: 'center',
    backgroundColor: '#e5e5e5',
    padding: 10,
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 'bold',
  },
  checklistItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
    borderBottom: '1px solid #ccc',
    borderLeft: '1px solid #ccc',
    borderRight: '1px solid #ccc',
    alignItems: 'center',
    minHeight: 36,
  },
  checklistText: { fontSize: 12, fontWeight: 'normal' },
  radioContainer: { flexDirection: 'row', alignItems: 'center' },
  radioText: { fontSize: 11, marginLeft: 6, marginRight: 12 },
  radioButton: {
    width: 12,
    height: 12,
    borderRadius: 6,
    border: '2px solid #ccc',
    marginLeft: 5,
    padding: 1,
  },
  radioButtonSelectedYes: { backgroundColor: '#28a745' },
  radioButtonSelectedNo: { backgroundColor: '#dc3545' },
  comments: {
    border: '1px solid #9CA3AF',
    padding: 12,
    marginBottom: 10,
    marginTop: 4,
  },
  underline: {
    borderBottomWidth: 1,
    borderBottomColor: '#9CA3AF',
    marginVertical: 5,
  },
  footer: {
    border: '1px solid #ccc',
    padding: 10,
    marginTop: 10,
    fontSize: 10,
    color: '#6B7280',
  },
});

// ─── Helpers ───
const getAnswer = (jsonData, question) => {
  if (!Array.isArray(jsonData)) return '';
  const item = jsonData.find((d) => d.question === question);
  return item?.answer || '';
};

// ─── PDF Checklist Row (matches WDS style) ───
const PDFChecklistItem = ({ label, value, options }) => (
  <View style={styles.checklistItem}>
    <Text style={styles.checklistText}>{label}</Text>
    <View style={styles.radioContainer}>
      {options.map((opt) => {
        const isSelected = value === opt;
        const isNegative = opt === 'No' || opt === 'Poor';
        return (
          <View key={opt} style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={styles.radioText}>{opt.toUpperCase()}</Text>
            <View
              style={[
                styles.radioButton,
                isSelected && (isNegative ? styles.radioButtonSelectedNo : styles.radioButtonSelectedYes),
              ]}
            />
          </View>
        );
      })}
    </View>
  </View>
);

// ─── PDF Document ───
const TrailerPDF = ({ rowData }) => {
  const json = rowData?.json_data || [];

  const personAssigned = getAnswer(json, 'Name of Person Assigned To') || '-';
  const trailerUnit = getAnswer(json, 'Trailer Unit #') || '-';
  const lastKnownJob = getAnswer(json, 'Last Known Job Number') || '-';
  const trailerType = getAnswer(json, 'Trailer Type') || '-';
  const filledDate = rowData?.filled_date || '-';
  const observerName = rowData?.observer_name || '-';
  const comments = getAnswer(json, 'Comments - Any Physical Damage') || 'No comments provided.';

  return (
    <Document>
      {/* ===== PAGE 1: Header + Info + Assignment + Compliance ===== */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src={LOgo} style={styles.logo} />
          <Text style={styles.title}>TRAILER INSPECTION FORM</Text>
          <View style={{ width: 100 }} />
        </View>

        {/* Person Assigned + Date */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <View style={{ flex: 1, border: '1px solid #9CA3AF', padding: 5 }}>
            <Text style={{ fontWeight: 'bold' }}>Person Assigned To:</Text>
            <Text>{personAssigned}</Text>
            <View style={{ borderBottom: '1px solid #9CA3AF', marginTop: 4 }} />
          </View>
          <View style={{ flex: 1, border: '1px solid #9CA3AF', padding: 5 }}>
            <Text style={{ fontWeight: 'bold' }}>Date:</Text>
            <Text>{filledDate}</Text>
            <View style={{ borderBottom: '1px solid #9CA3AF', marginTop: 4 }} />
          </View>
        </View>

        {/* Trailer Unit + Job Number */}
        <View style={{ flexDirection: 'row', marginBottom: 14 }}>
          <View style={{ flex: 1, border: '1px solid #9CA3AF', padding: 5 }}>
            <Text style={{ fontWeight: 'bold' }}>Trailer Unit #:</Text>
            <Text>{trailerUnit}</Text>
            <View style={{ borderBottom: '1px solid #9CA3AF', marginTop: 4 }} />
          </View>
          <View style={{ flex: 1, border: '1px solid #9CA3AF', padding: 5 }}>
            <Text style={{ fontWeight: 'bold' }}>Last Known Job Number:</Text>
            <Text>{lastKnownJob}</Text>
            <View style={{ borderBottom: '1px solid #9CA3AF', marginTop: 4 }} />
          </View>
        </View>

        {/* 1. Assignment - Trailer Type */}
        <Text style={styles.sectionHeader}>ASSIGNMENT - TRAILER TYPE</Text>
        <PDFChecklistItem label="Trailer Type" value={trailerType} options={['CT', 'TV', 'TT', 'ET', 'UT']} />

        <View style={{ marginBottom: 14 }} />

        {/* 2. Compliance */}
        <Text style={styles.sectionHeader}>REGISTRATION & COMPLIANCE</Text>
        <PDFChecklistItem label="Is License Registration Current?" value={getAnswer(json, 'Is License Registration Current?')} options={['Yes', 'No']} />
        <PDFChecklistItem label="Insurance Card / Current Cards?" value={getAnswer(json, 'Insurance Card / Current Cards?')} options={['Yes', 'No']} />
        <PDFChecklistItem label="Does ball size match to trailer hitch size?" value={getAnswer(json, 'Does ball size match to trailer hitch size?')} options={['Yes', 'No']} />
        <PDFChecklistItem label="Does your trailer need Fire Extinguisher? (No for utility trailers)" value={getAnswer(json, 'Does your trailer need Fire Extinguisher? (No for utility trailers)')} options={['Yes', 'No']} />

        <View style={{ marginBottom: 14 }} />

        {/* 3. Condition */}
        <Text style={styles.sectionHeader}>PHYSICAL CONDITION ASSESSMENT</Text>
        <PDFChecklistItem label="Tire Condition" value={getAnswer(json, 'Tire Condition')} options={['Good', 'Fair', 'Poor']} />
        <PDFChecklistItem label="Axel Condition" value={getAnswer(json, 'Axel Condition')} options={['Good', 'Fair', 'Poor']} />
        <PDFChecklistItem label="Brake Condition" value={getAnswer(json, 'Brake Condition')} options={['Good', 'Fair', 'Poor']} />
        <PDFChecklistItem label="Jack and Safety Chain Condition" value={getAnswer(json, 'Jack and Safety Chain Condition')} options={['Good', 'Fair', 'Poor']} />
      </Page>

      {/* ===== PAGE 2: Lights & Safety + Comments ===== */}
      <Page size="A4" style={styles.page}>
        {/* 4. Lights & Safety */}
        <Text style={styles.sectionHeader}>LIGHTS & SAFETY CHECK</Text>
        <PDFChecklistItem label="Turn Signal Lights" value={getAnswer(json, 'Turn Signal Lights')} options={['Yes', 'No']} />
        <PDFChecklistItem label="Brake Lights" value={getAnswer(json, 'Brake Lights')} options={['Yes', 'No']} />
        <PDFChecklistItem label="Proper Tie Downs" value={getAnswer(json, 'Proper Tie Downs')} options={['Yes', 'No']} />
        <PDFChecklistItem label="Trailer Hitch and Door Locks" value={getAnswer(json, 'Trailer Hitch and Door Locks')} options={['Yes', 'No']} />

        <View style={{ marginBottom: 14 }} />

        {/* 5. Comments */}
        <View style={styles.comments}>
          <Text style={{ fontWeight: 'bold', marginBottom: 5, fontSize: 14 }}>COMMENTS - ANY PHYSICAL DAMAGE</Text>
          <Text style={{ fontSize: 12 }}>{comments}</Text>
          <View style={styles.underline} />
        </View>

        {/* Footer Note */}
        <View style={styles.footer}>
          <Text>By submitting this form, you confirm that the information provided is accurate and complete to the best of your knowledge.</Text>
        </View>

        {/* Observer */}
        <View style={{ flexDirection: 'row', marginTop: 10 }}>
          <View style={{ flex: 1, border: '1px solid #9CA3AF', padding: 5 }}>
            <Text style={{ fontWeight: 'bold' }}>Observer:</Text>
            <Text>{observerName}</Text>
            <View style={{ borderBottom: '1px solid #9CA3AF', marginTop: 4 }} />
          </View>
          <View style={{ flex: 1 }} />
        </View>
      </Page>
    </Document>
  );
};

// ─── HTML Radio Display (same style as WDS checklist) ───
const HTMLRadioRow = ({ label, value, options }) => (
  <div className="flex justify-between items-center px-2 border py-4">
    <p className="text-sm font-inter text-left">{label}</p>
    <div className="flex space-x-2">
      {options.map((opt) => {
        const isSelected = value === opt;
        const isNegative = opt === 'No' || opt === 'Poor';
        return (
          <label key={opt} className="flex items-center">
            <input
              type="radio"
              name={`${label}-view`}
              value={opt}
              checked={isSelected}
              readOnly
              className={`mr-1 ${isSelected ? (isNegative ? 'accent-red-600' : 'accent-green-600') : ''}`}
            />
            <span
              className={
                isSelected
                  ? isNegative
                    ? 'text-red-600 font-bold'
                    : 'text-green-600 font-bold'
                  : 'text-gray-400'
              }
            >
              {opt}
            </span>
          </label>
        );
      })}
    </div>
  </div>
);

// ─── Main Modal Component ───
const ViewTrailerModal = ({ formType, rowData, setFormView }) => {
  const formRef = useRef(null);
  const json = rowData?.json_data || [];

  const personAssigned = getAnswer(json, 'Name of Person Assigned To');
  const trailerUnit = getAnswer(json, 'Trailer Unit #');
  const lastKnownJob = getAnswer(json, 'Last Known Job Number');
  const trailerType = getAnswer(json, 'Trailer Type');
  const filledDate = rowData?.filled_date || '-';
  const observerName = rowData?.observer_name || '-';
  const comments = getAnswer(json, 'Comments - Any Physical Damage') || 'No comments provided.';

  const complianceItems = [
    { label: 'Is License Registration Current?', value: getAnswer(json, 'Is License Registration Current?') },
    { label: 'Insurance Card / Current Cards?', value: getAnswer(json, 'Insurance Card / Current Cards?') },
    { label: 'Does ball size match to trailer hitch size?', value: getAnswer(json, 'Does ball size match to trailer hitch size?') },
    { label: 'Does your trailer need Fire Extinguisher? (No for utility trailers)', value: getAnswer(json, 'Does your trailer need Fire Extinguisher? (No for utility trailers)') },
  ];

  const conditionItems = [
    { label: 'Tire Condition', value: getAnswer(json, 'Tire Condition') },
    { label: 'Axel Condition', value: getAnswer(json, 'Axel Condition') },
    { label: 'Brake Condition', value: getAnswer(json, 'Brake Condition') },
    { label: 'Jack and Safety Chain Condition', value: getAnswer(json, 'Jack and Safety Chain Condition') },
  ];

  const lightsItems = [
    { label: 'Turn Signal Lights', value: getAnswer(json, 'Turn Signal Lights') },
    { label: 'Brake Lights', value: getAnswer(json, 'Brake Lights') },
    { label: 'Proper Tie Downs', value: getAnswer(json, 'Proper Tie Downs') },
    { label: 'Trailer Hitch and Door Locks', value: getAnswer(json, 'Trailer Hitch and Door Locks') },
  ];

  useEffect(() => {
    if (formRef.current) formRef.current.scrollTop = 0;
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setFormView(false);
  };

  const generatePDF = async () => {
    try {
      const blob = await import('@react-pdf/renderer').then(({ pdf }) =>
        pdf(<TrailerPDF rowData={rowData} />).toBlob()
      );
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <div onClick={handleBackdropClick} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative w-full max-w-4xl flex flex-col justify-center items-center p-6 bg-white rounded-lg border border-gray-300 shadow-md overflow-y-auto custom-scrollbar max-h-[95vh]">
        {/* Top bar: Print + Close */}
        <div className="w-full flex justify-between items-end">
          <button
            onClick={generatePDF}
            className="bg-red-800 text-white px-4 py-1 rounded hover:bg-green-600"
          >
            Print
          </button>
          <button onClick={() => setFormView(false)} className="text-gray-600 hover:text-black">
            <CloseIcon />
          </button>
        </div>

        {/* HTML Form View */}
        <div ref={formRef} className="w-[90%] mt-4 mx-auto overflow-y-auto custom-scrollbar p-4">
          {/* Header */}
          <div className="border-b border-gray-700 pb-2 mb-4">
            <div className="flex w-full justify-between">
              <img src={NtactLogo} alt="NTACT Logo" />
              <div>
                <h2 className="text-lg font-semibold">TRAILER INSPECTION FORM</h2>
              </div>
              <div className="mr-24"></div>
            </div>
          </div>

          {/* Row 1: Person Assigned + Date */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border border-[#9CA3AF] p-1">
              <p className="text-sm font-inter font-semibold text-left">Person Assigned To:</p>
              <p className="text-left font-inter p-1 px-2">{personAssigned}</p>
              <hr className="border-t border-[#9CA3AF]" />
            </div>
            <div className="border border-[#9CA3AF] p-1">
              <p className="text-sm font-inter font-semibold text-left">Date:</p>
              <p className="text-left font-inter p-1 px-2">{filledDate}</p>
              <hr className="border-t border-[#9CA3AF]" />
            </div>
          </div>

          {/* Row 2: Trailer Unit + Job Number */}
          <div className="border border-[#9CA3AF] p-1 mb-4">
            <p className="text-sm font-inter font-semibold text-left">Trailer Unit #:</p>
            <p className="text-left font-inter p-1 px-2">{trailerUnit}</p>
            <hr className="border-t border-[#9CA3AF]" />
            <p className="text-sm font-inter font-semibold text-left">Last Known Job Number:</p>
            <p className="text-left font-inter p-1 px-2">{lastKnownJob}</p>
            <hr className="border-t border-[#9CA3AF]" />
          </div>

          {/* 1. Assignment - Trailer Type */}
          <div className="mb-4">
            <p className="text-sm font-inter font-semibold text-center bg-gray-200 p-2 pb-1 mb-2">
              ASSIGNMENT - TRAILER TYPE
            </p>
            <div className="grid grid-cols-1">
              <HTMLRadioRow label="Trailer Type" value={trailerType} options={['CT', 'TV', 'TT', 'ET', 'UT']} />
            </div>
          </div>

          {/* 2. Compliance */}
          <div className="mb-4">
            <p className="text-sm font-inter font-semibold text-center bg-gray-200 p-2 pb-1 mb-2">
              REGISTRATION & COMPLIANCE
            </p>
            <div className="grid grid-cols-1">
              {complianceItems.map((item, idx) => (
                <HTMLRadioRow key={idx} label={item.label} value={item.value} options={['Yes', 'No']} />
              ))}
            </div>
          </div>

          {/* 3. Condition */}
          <div className="mb-4">
            <p className="text-sm font-inter font-semibold text-center bg-gray-200 p-2 pb-1 mb-2">
              PHYSICAL CONDITION ASSESSMENT
            </p>
            <div className="grid grid-cols-1">
              {conditionItems.map((item, idx) => (
                <HTMLRadioRow key={idx} label={item.label} value={item.value} options={['Good', 'Fair', 'Poor']} />
              ))}
            </div>
          </div>

          {/* 4. Lights & Safety */}
          <div className="mb-4">
            <p className="text-sm font-inter font-semibold text-center bg-gray-200 p-2 pb-1 mb-2">
              LIGHTS & SAFETY CHECK
            </p>
            <div className="grid grid-cols-1">
              {lightsItems.map((item, idx) => (
                <HTMLRadioRow key={idx} label={item.label} value={item.value} options={['Yes', 'No']} />
              ))}
            </div>
          </div>

          {/* 5. Comments */}
          <div className="mb-4">
            <div className="text-[16px] my-4 text-left font-semibold">COMMENTS - ANY PHYSICAL DAMAGE</div>
            <div className="border border-[#9CA3AF] p-1">
              <p className="text-sm text-wrap text-gray-700 text-left font-inter py-2 px-3">{comments}</p>
              <hr className="border-t border-[#9CA3AF]" />
            </div>
          </div>

          {/* Footer Note */}
          <div className="mb-4 p-4 border border-gray-300 rounded">
            <p className="text-sm font-inter text-left text-gray-500">
              By submitting this form, you confirm that the information provided is accurate and complete to the best of your knowledge.
            </p>
          </div>

          {/* Observer */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-[#9CA3AF] p-1">
              <p className="text-sm font-inter font-semibold text-left">Observer:</p>
              <p className="text-left font-inter p-1 px-2">{observerName}</p>
              <hr className="border-t border-[#9CA3AF]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTrailerModal;
